import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, Loader2 } from "lucide-react";
import { api } from "@/lib/apiClient";
import { trackCompleteRegistration } from "@/lib/tracking";
import { log } from "@/lib/log";

const BLOCAGE_OPTIONS = [
  { value: "carriere_finances", label: "Carrière & Finances", emoji: "💼" },
  { value: "relations_famille", label: "Relations & Famille", emoji: "❤️" },
  { value: "cadre_vie_habitat", label: "Cadre de vie & Habitat", emoji: "🏠" },
  { value: "confiance_identite", label: "Confiance & Identité", emoji: "🧠" },
  { value: "plusieurs", label: "Plusieurs domaines à la fois", emoji: "🔀" },
];

const EXPERIENCE_OPTIONS = [
  { value: "experience", label: "Oui, j'ai de l'expérience" },
  { value: "premiere_fois", label: "Non, c'est ma première fois" },
  { value: "sans_resultats", label: "J'ai essayé mais sans résultats concrets" },
];

const MONTHS = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 90 }, (_, i) => currentYear - 18 - i);
const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

const GOLD = "#B8860B";
const BG = "#0a0a0a";

export default function PreDiagnostic() {
  const [params] = useSearchParams();
  const prenomFromUrl = (params.get("prenom") || "").trim();
  const whatsappFromUrl = (params.get("whatsapp") || "").trim();

  const [prenom, setPrenom] = useState(prenomFromUrl || "");
  const [whatsapp, setWhatsapp] = useState(whatsappFromUrl || "");

  const [step, setStep] = useState(0); // 0..4 questions, 5 = confirmation
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [answers, setAnswers] = useState({
    day: "", month: "", year: "",
    blocage: "",
    situation: "",
    priorite: "",
    experience: "",
  });

  useEffect(() => {
    // Fallback : hydrate depuis localStorage si URL vide (setState fonctionnel → aucune dépendance sur l'état courant)
    try {
      const p = localStorage.getItem("bilan_prenom");
      if (p) setPrenom((cur) => cur || p);
      const w = localStorage.getItem("bilan_whatsapp");
      if (w) setWhatsapp((cur) => cur || w);
    } catch (e) {
      log.warn("[pre-diagnostic] localStorage indisponible :", e);
    }
    // Empêcher indexation
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    const prevTitle = document.title;
    document.title = "Pré-diagnostic — Rodrigue Cassildé";
    return () => {
      document.head.removeChild(meta);
      document.title = prevTitle;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const displayName = prenom || "vous";
  const totalQuestions = 5;

  // Progress: étape 1 (=/bilan) déjà faite → base 50 %. On progresse jusqu'à 100 % au fil des Q.
  const progressPct = useMemo(() => {
    if (step >= totalQuestions) return 100;
    return Math.round(50 + (step / totalQuestions) * 50);
  }, [step]);

  const validateStep = () => {
    setError("");
    switch (step) {
      case 0:
        if (!answers.day || !answers.month || !answers.year) {
          setError("Merci d'indiquer votre date de naissance complète.");
          return false;
        }
        return true;
      case 1:
        if (!answers.blocage) { setError("Choisissez un domaine."); return false; }
        return true;
      case 2:
        if (answers.situation.trim().length < 10) { setError("Décrivez votre situation en quelques mots (10 caractères min)."); return false; }
        return true;
      case 3:
        if (answers.priorite.trim().length < 10) { setError("Précisez ce que vous voulez changer (10 caractères min)."); return false; }
        return true;
      case 4:
        if (!answers.experience) { setError("Choisissez une option."); return false; }
        return true;
      default:
        return true;
    }
  };

  const next = () => { if (validateStep()) setStep((s) => s + 1); };
  const back = () => { setError(""); setStep((s) => Math.max(0, s - 1)); };

  const submit = async () => {
    if (!validateStep()) return;
    setLoading(true);
    setError("");
    try {
      const birthdate = `${String(answers.day).padStart(2,"0")}/${String(answers.month).padStart(2,"0")}/${answers.year}`;
      const blocageLabel = BLOCAGE_OPTIONS.find((o) => o.value === answers.blocage)?.label || answers.blocage;
      const experienceLabel = EXPERIENCE_OPTIONS.find((o) => o.value === answers.experience)?.label || answers.experience;

      const res = await api.post("/pre-diagnostic", {
        prenom: prenom || "Prospect",
        whatsapp: whatsapp || null,
        birthdate,
        blocage_domain: blocageLabel,
        situation: answers.situation.trim(),
        priorite_changement: answers.priorite.trim(),
        experience: experienceLabel,
      });
      const capiEventId = res?.data?.capi_event_id;
      // CompleteRegistration — eventID partagé avec le CAPI serveur pour dédup
      trackCompleteRegistration({ value: 0, currency: "EUR", content_name: "pre_diagnostic_qualified", eventID: capiEventId });
      setStep(totalQuestions); // confirmation
    } catch (err) {
      setError("Une erreur est survenue. Merci de réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="prediag-page" className="min-h-screen text-white antialiased" style={{ backgroundColor: BG }}>
      {/* HEADER */}
      <header className="px-5 pt-12 md:pt-16 pb-8 max-w-3xl mx-auto text-center">
        <p className="text-[11px] md:text-xs uppercase tracking-[0.25em] mb-4" style={{ color: GOLD }}>
          Étape 2 sur 2 — Questionnaire de pré-diagnostic
        </p>
        <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-[1.1] mb-5">
          Parfait, <span style={{ color: GOLD }}>{displayName}</span> !
          <br className="hidden sm:inline" /> Une dernière étape avant votre bilan.
        </h1>
        <p className="text-white/70 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
          Répondez à ces 5 questions en 2 minutes. Cela permettra à Rodrigue de préparer votre
          bilan personnalisé et d'aller droit au but dès la première minute.
        </p>

        {/* Barre de progression */}
        <div className="mt-8 max-w-md mx-auto">
          <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: "rgba(255,255,255,0.08)" }}>
            <motion.div
              data-testid="prediag-progress-bar"
              animate={{ width: `${progressPct}%` }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="h-full rounded-full"
              style={{ backgroundColor: GOLD }}
            />
          </div>
          <p className="mt-2 text-xs text-white/50">{progressPct}%</p>
        </div>
      </header>

      {/* CARTE QUESTION / CONFIRMATION */}
      <main className="px-5 pb-16">
        <div
          className="max-w-xl mx-auto p-6 md:p-10 rounded-xl border"
          style={{ borderColor: "rgba(184,134,11,0.25)", backgroundColor: "#0d0d0d" }}
        >
          <AnimatePresence mode="wait">
            {step === 0 && (
              <QuestionCard key="q0" title="Votre date de naissance" hint="Cette info permet de calculer votre profil vibratoire avant l'appel.">
                <div className="grid grid-cols-3 gap-3">
                  <SelectField testid="prediag-day" label="Jour" value={answers.day} onChange={(v) => setAnswers({ ...answers, day: v })} options={DAYS.map(String)} />
                  <SelectField testid="prediag-month" label="Mois" value={answers.month} onChange={(v) => setAnswers({ ...answers, month: v })} options={MONTHS.map((m, i) => ({ value: String(i + 1), label: m }))} />
                  <SelectField testid="prediag-year" label="Année" value={answers.year} onChange={(v) => setAnswers({ ...answers, year: v })} options={YEARS.map(String)} />
                </div>
              </QuestionCard>
            )}

            {step === 1 && (
              <QuestionCard key="q1" title="Dans quel domaine ressentez-vous le plus de blocage en ce moment ?">
                <div className="grid gap-3">
                  {BLOCAGE_OPTIONS.map((opt) => (
                    <OptionButton
                      key={opt.value}
                      testid={`prediag-blocage-${opt.value}`}
                      selected={answers.blocage === opt.value}
                      onClick={() => setAnswers({ ...answers, blocage: opt.value })}
                    >
                      <span className="text-xl mr-3">{opt.emoji}</span> {opt.label}
                    </OptionButton>
                  ))}
                </div>
              </QuestionCard>
            )}

            {step === 2 && (
              <QuestionCard key="q2" title="Comment décririez-vous votre situation actuelle ?">
                <TextArea
                  testid="prediag-situation"
                  value={answers.situation}
                  onChange={(v) => setAnswers({ ...answers, situation: v })}
                  placeholder="Ex: Je me sens bloquée depuis 2 ans, j'ai l'impression de ne pas avancer malgré mes efforts..."
                />
              </QuestionCard>
            )}

            {step === 3 && (
              <QuestionCard key="q3" title="Qu'est-ce que vous souhaitez changer en priorité ?">
                <TextArea
                  testid="prediag-priorite"
                  value={answers.priorite}
                  onChange={(v) => setAnswers({ ...answers, priorite: v })}
                  placeholder="Ex: Je veux reprendre confiance en moi et savoir enfin quelle direction prendre..."
                />
              </QuestionCard>
            )}

            {step === 4 && (
              <QuestionCard key="q4" title="Avez-vous déjà travaillé avec un coach ou exploré la numérologie / Feng Shui ?">
                <div className="grid gap-3">
                  {EXPERIENCE_OPTIONS.map((opt) => (
                    <OptionButton
                      key={opt.value}
                      testid={`prediag-experience-${opt.value}`}
                      selected={answers.experience === opt.value}
                      onClick={() => setAnswers({ ...answers, experience: opt.value })}
                    >
                      {opt.label}
                    </OptionButton>
                  ))}
                </div>
              </QuestionCard>
            )}

            {step === totalQuestions && (
              <motion.div
                key="done"
                data-testid="prediag-success"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="text-center py-4"
              >
                <div
                  className="mx-auto mb-6 h-16 w-16 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: "rgba(184,134,11,0.15)" }}
                >
                  <Check size={32} style={{ color: GOLD }} strokeWidth={2.5} />
                </div>
                <h2 className="font-serif text-2xl md:text-3xl mb-4">
                  C'est confirmé, <span style={{ color: GOLD }}>{displayName}</span> ! 🙏
                </h2>
                <p className="text-white/75 leading-relaxed">
                  Rodrigue a reçu votre dossier. Il vous contactera personnellement sur WhatsApp
                  dans les 24 prochaines heures pour fixer la date de votre bilan.
                </p>
                <p className="mt-4 text-sm text-white/55">
                  En attendant, regardez votre boîte WhatsApp.
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* NAV BUTTONS — cachés sur l'écran de confirmation */}
          {step < totalQuestions && (
            <div className="mt-8">
              {error && <p data-testid="prediag-error" className="mb-4 text-sm text-red-400">{error}</p>}
              <div className="flex items-center gap-3">
                {step > 0 && (
                  <button
                    type="button"
                    data-testid="prediag-back"
                    onClick={back}
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm border border-white/15 text-white/80 hover:text-white hover:border-white/30 transition-colors"
                  >
                    <ArrowLeft size={16} /> Retour
                  </button>
                )}
                {step < totalQuestions - 1 ? (
                  <button
                    type="button"
                    data-testid="prediag-next"
                    onClick={next}
                    className="ml-auto inline-flex items-center gap-2 px-7 py-3 rounded-full text-sm md:text-base font-semibold uppercase tracking-wider transition-transform hover:scale-[1.02] active:scale-[0.98]"
                    style={{ backgroundColor: GOLD, color: BG }}
                  >
                    Suivant <ArrowRight size={16} />
                  </button>
                ) : (
                  <button
                    type="button"
                    data-testid="prediag-submit"
                    onClick={submit}
                    disabled={loading}
                    className="ml-auto inline-flex items-center gap-2 px-7 py-3 rounded-full text-sm md:text-base font-semibold uppercase tracking-wider transition-transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60"
                    style={{ backgroundColor: GOLD, color: BG }}
                  >
                    {loading ? <Loader2 size={16} className="animate-spin" /> : <>Envoyer et confirmer mon bilan <ArrowRight size={16} /></>}
                  </button>
                )}
              </div>
              <p className="mt-5 text-center text-xs text-white/40">
                Question {step + 1} sur {totalQuestions}
              </p>
            </div>
          )}
        </div>
      </main>

      <footer className="px-5 py-8 text-center border-t" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
        <p className="text-xs text-white/40">
          © Académie AETHERIS — Rodrigue Cassildé |{" "}
          <a href="https://serenite-essence.fr" className="hover:text-white/70 transition-colors">serenite-essence.fr</a>
        </p>
      </footer>
    </div>
  );
}

/* ============================ Sub-components ============================ */

function QuestionCard({ title, hint, children }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 24 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -24 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
    >
      <h2 className="font-serif text-xl md:text-2xl leading-snug mb-4">{title}</h2>
      {hint && <p className="text-sm text-white/50 mb-5">{hint}</p>}
      {children}
    </motion.div>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  const opts = options.map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.15em] text-white/50 mb-2">{label}</span>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent border border-white/15 focus:border-[#B8860B] rounded-md py-2.5 px-3 text-white outline-none transition-colors [color-scheme:dark]"
      >
        <option value="">—</option>
        {opts.map((o) => (
          <option key={o.value} value={o.value} style={{ background: "#0a0a0a" }}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

function OptionButton({ selected, onClick, children, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      aria-pressed={selected}
      className={`w-full text-left px-4 py-3.5 rounded-lg border text-[15px] transition-all ${
        selected ? "text-white" : "text-white/85 hover:text-white"
      }`}
      style={{
        borderColor: selected ? "#B8860B" : "rgba(255,255,255,0.12)",
        backgroundColor: selected ? "rgba(184,134,11,0.14)" : "transparent",
      }}
    >
      {children}
    </button>
  );
}

function TextArea({ value, onChange, placeholder, testid }) {
  return (
    <textarea
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={4}
      className="w-full bg-transparent border border-white/15 focus:border-[#B8860B] rounded-md py-3 px-3 text-white placeholder-white/30 outline-none transition-colors resize-y min-h-[110px]"
    />
  );
}
